'use client';

import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp, Car, Building2, Briefcase, CalendarCheck, Banknote } from 'lucide-react';
import {
  LinkToAppointmentToggle,
  LinkToVehicleToggle,
  LinkingModeSelector,
  SupplierDropdown,
  ProcurementPreCapture,
  type LinkToAppointmentToggleProps,
  type LinkToVehicleToggleProps,
  type LinkingModeSelectorProps,
  type SupplierDropdownProps,
  type ProcurementPreCaptureProps,
  type LinkingMode,
  type VehicleExpenseSubType,
} from './LinkToggleSection';
import type { Supplier } from './expense';
import type { ChildEntryType } from '../types';

export interface SelectionSectionsAccordionProps {
  // ── Collapse state ──
  selectionExpanded: boolean;
  setSelectionExpanded: (v: boolean) => void;

  // ── Visibility flags (drive hasSelectionContent) ──
  showVehicleLinkToggle: boolean;
  isSmartCaptureLinkingMode: boolean;
  showLinkToggle: boolean | '' | 0 | null; // truthy check used
  canSwitchEntryTypeInCamera: boolean;
  isProcurementType: boolean;
  linkToWorkItem: boolean;
  isFormVisible: boolean;
  /** User has a vehicle assigned — gates the Vehicle Expense button */
  hasVehicleAssigned: boolean;

  // ── Summary chip inputs ──
  effectiveVehicleRegistration: string | null | undefined;
  linkToVehicle: boolean;
  vehicleRegistration: string | null | undefined;
  linkingMode: LinkingMode;
  vehicleSubType: VehicleExpenseSubType;
  selectedSupplier: Supplier | null;
  linkableWorkItemNumber: string | null | undefined;
  isJobLinkedExpense: boolean;
  selectedCustomer: { id: number; company_name: string | null; contact_person: string | null } | null;
  selectedJob: { id: string; item_number: string; title: string; contact_name?: string | null } | null;

  // ── Child component prop bundles ──
  vehicleToggleProps: LinkToVehicleToggleProps;
  linkingModeProps: LinkingModeSelectorProps;
  supplierDropdownProps: SupplierDropdownProps;
  appointmentToggleProps: LinkToAppointmentToggleProps;
  procurementPreCaptureProps: ProcurementPreCaptureProps;
  /** When true, lock the linking mode selector to vehicle expense only (Fuel/Toll/Maintenance) */
  lockToVehicleExpense?: boolean;
  /** When true, the linking mode was preselected by the launching tile —
   *  hide the 6-button selector entirely (mode-specific pickers still show). */
  hideLinkingModeSelector?: boolean;
}

/**
 * Collapsible accordion grouping all "selection" sections (vehicle link,
 * Smart Capture linking mode, supplier dropdown, appointment link, and
 * pre-capture procurement supplier/PO selection).
 *
 * Collapses into a compact summary-chip bar after the first photo is captured
 * (driven by the parent's `selectionExpanded` state). When expanded, renders
 * the relevant child toggle components in order.
 */
export function SelectionSectionsAccordion({
  selectionExpanded,
  setSelectionExpanded,
  showVehicleLinkToggle,
  isSmartCaptureLinkingMode,
  showLinkToggle,
  canSwitchEntryTypeInCamera,
  isProcurementType,
  linkToWorkItem,
  isFormVisible,
  hasVehicleAssigned,
  effectiveVehicleRegistration,
  linkToVehicle,
  vehicleRegistration,
  linkingMode,
  vehicleSubType,
  selectedSupplier,
  linkableWorkItemNumber,
  isJobLinkedExpense,
  selectedCustomer,
  selectedJob,
  vehicleToggleProps,
  linkingModeProps,
  supplierDropdownProps,
  appointmentToggleProps,
  procurementPreCaptureProps,
  lockToVehicleExpense = false,
  hideLinkingModeSelector = false,
}: SelectionSectionsAccordionProps) {
  const hasSelectionContent =
    showVehicleLinkToggle ||
    isSmartCaptureLinkingMode ||
    !!showLinkToggle ||
    (canSwitchEntryTypeInCamera && isProcurementType && !linkToWorkItem && !isFormVisible);

  const summaryChips = useMemo<Array<{ icon: React.ReactNode; label: string }>>(() => {
    const chips: Array<{ icon: React.ReactNode; label: string }> = [];
    if (showVehicleLinkToggle && (effectiveVehicleRegistration || (linkToVehicle && vehicleRegistration))) {
      chips.push({ icon: <Car className="h-3 w-3" />, label: effectiveVehicleRegistration || vehicleRegistration || 'Vehicle' });
    }
    if (linkingMode) {
      const modeLabel = linkingMode === 'supplier' ? 'Supplier Document'
        : linkingMode === 'job' ? 'Job Expense'
        : linkingMode === 'both' ? 'Supplier & Job'
        : linkingMode === 'general' ? 'General Expense'
        : linkingMode === 'vehicle' ? (vehicleSubType ? vehicleSubType.charAt(0).toUpperCase() + vehicleSubType.slice(1) : 'Vehicle Expense')
        : linkingMode === 'wages' ? 'Casual Wages'
        : 'Expense';
      const modeIcon = linkingMode === 'vehicle' ? <Car className="h-3 w-3" />
        : linkingMode === 'wages' ? <Banknote className="h-3 w-3" />
        : <Briefcase className="h-3 w-3" />;
      chips.push({ icon: modeIcon, label: modeLabel });
    }
    if (selectedSupplier && (linkingMode === 'supplier' || linkingMode === 'both' || isProcurementType)) {
      chips.push({ icon: <Building2 className="h-3 w-3" />, label: selectedSupplier.company_name || selectedSupplier.name || 'Supplier' });
    }
    if (linkToWorkItem && linkableWorkItemNumber) {
      chips.push({ icon: <CalendarCheck className="h-3 w-3" />, label: linkableWorkItemNumber });
    }
    if (isJobLinkedExpense && !linkToWorkItem && selectedCustomer) {
      chips.push({ icon: <Building2 className="h-3 w-3" />, label: selectedCustomer.company_name || selectedCustomer.contact_person || 'Customer' });
    }
    if (isJobLinkedExpense && !linkToWorkItem && selectedJob) {
      chips.push({ icon: <Briefcase className="h-3 w-3" />, label: selectedJob.item_number || selectedJob.title || 'Job' });
    }
    return chips;
  }, [
    showVehicleLinkToggle, effectiveVehicleRegistration, linkToVehicle, vehicleRegistration,
    linkingMode, vehicleSubType, selectedSupplier, isProcurementType, linkToWorkItem, linkableWorkItemNumber,
    isJobLinkedExpense, selectedCustomer, selectedJob,
  ]);

  if (!hasSelectionContent) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Collapsed summary bar — single click to expand */}
      <button
        type="button"
        onClick={() => setSelectionExpanded(!selectionExpanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 shrink-0">
            {selectionExpanded ? 'Selections' : 'Linked to'}
          </span>
          {!selectionExpanded && summaryChips.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              {summaryChips.map((chip, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 truncate max-w-[120px]">
                  {chip.icon}
                  <span className="truncate">{chip.label}</span>
                </span>
              ))}
            </div>
          )}
          {!selectionExpanded && summaryChips.length === 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">Tap to configure</span>
          )}
        </div>
        {selectionExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* Expanded selection sections */}
      {selectionExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* ── Link to my vehicle toggle + vehicle search dropdown ── */}
          <LinkToVehicleToggle {...vehicleToggleProps} />

          {/* ── Linking Mode Selector for Smart Capture (6-button grid, 2 rows) ── */}
          {isSmartCaptureLinkingMode && (
            <LinkingModeSelector
              {...linkingModeProps}
              hasVehicleAssigned={hasVehicleAssigned}
              vehicleSubType={vehicleSubType}
              setVehicleSubType={linkingModeProps.setVehicleSubType}
              lockToVehicleExpense={lockToVehicleExpense}
              hideModeButtons={hideLinkingModeSelector}
            />
          )}

          {/* ── Supplier dropdown (shown for 'supplier' and 'both' linking modes) ── */}
          {isSmartCaptureLinkingMode && (linkingMode === 'supplier' || linkingMode === 'both') && (
            <SupplierDropdown {...supplierDropdownProps} />
          )}

          {/* ── Link to active appointment toggle + customer/job search ── */}
          <LinkToAppointmentToggle {...appointmentToggleProps} />

          {/* ── Pre-capture Supplier + PO Selection (procurement types, not linked to appointment) ── */}
          {/* Hidden for zero-trust users — they capture as general expense; reclassification happens in back-office */}
          {canSwitchEntryTypeInCamera && <ProcurementPreCapture {...procurementPreCaptureProps} />}
        </div>
      )}
    </div>
  );
}
